import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, addDoc, doc, getDoc } from "firebase/firestore";
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { db } from "../firebase";
import { useCuerpo } from "../context/CuerpoContext";
import PartesLista from "./PartesLista";

/**
 * ✅ En tu Auth: 16309@de.cl
 * - Si el usuario escribe email (tiene @) se usa tal cual
 * - Si escribe solo usuario (16309) => 16309@de.cl
 */
const PARTES_EMAIL_DOMAIN = "de.cl";
function usuarioToEmail(input) {
  const v = String(input || "").trim();
  if (!v) return "";
  if (v.includes("@")) return v.toLowerCase();
  return `${v}@${PARTES_EMAIL_DOMAIN}`.toLowerCase();
}

export default function PartesSeleccionCompania() {
  const navigate = useNavigate();
  const { cuerpoId, cuerpoSlug } = useCuerpo();
  const partesBase = `/${cuerpoSlug}/partes`;
  const auth = getAuth();

  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("login"); // login | menu

  // login inputs
  const [usuario, setUsuario] = useState("");
  const [contrasena, setContrasena] = useState("");
  const [authError, setAuthError] = useState("");

  // perfil permisos
  const [perfil, setPerfil] = useState(null); // {activo, usuario, rol, compania, cuerpo_id}
  const [userUid, setUserUid] = useState("");

  // ✅ responsive
  const [isSmall, setIsSmall] = useState(() =>
    typeof window === "undefined" ? false : window.innerWidth < 820
  );

  useEffect(() => {
    function onResize() {
      setIsSmall(window.innerWidth < 820);
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ===== Auditoría =====
  async function logAuditoria({
    accion,
    compania: cia = null,
    parteId = null,
    folio = null,
    extra = null,
  }) {
    try {
      await addDoc(collection(db, "auditoria_partes"), {
        at: new Date(),
        usuarioId: userUid || auth.currentUser?.uid || null,
        usuario: perfil?.usuario || usuario || null,
        rol: perfil?.rol || null,
        cuerpo_id: perfil?.cuerpo_id || null,
        compania: cia,
        accion,
        parteId,
        folio,
        extra,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
    } catch (e) {
      console.warn("⚠️ No se pudo registrar auditoría:", e);
    }
  }

  async function cargarPerfilPorUid(uid) {
    const ref = doc(db, "usuarios_partes", uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  }

  // ===== Sesión Auth =====
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setUserUid("");
        setPerfil(null);
        setStep("login");
        setLoading(false);
        return;
      }

      if (u.isAnonymous) {
        setUserUid(u.uid);
        setPerfil(null);
        setStep("login");
        setLoading(false);
        return;
      }

      setLoading(true);
      setUserUid(u.uid);

      try {
        const p = await cargarPerfilPorUid(u.uid);

        if (!p) {
          setPerfil(null);
          setStep("login");
          setAuthError(
            "Tu usuario existe, pero NO está autorizado para Partes (falta perfil)."
          );
          await logAuditoria({ accion: "LOGIN_OK_BUT_NO_PROFILE" });
          await signOut(auth);
          setLoading(false);
          return;
        }

        const perfilCuerpo = String(p.cuerpo_id || p.cuerpoId || "").trim().toLowerCase();
        const cuerpoActual = String(cuerpoId || "").trim().toLowerCase();
        if (perfilCuerpo && cuerpoActual && perfilCuerpo !== cuerpoActual) {
          setPerfil(null);
          setStep("login");
          setAuthError("Este usuario de Partes pertenece a otro Cuerpo de Bomberos.");
          await signOut(auth);
          setLoading(false);
          return;
        }

        if (p.activo === false) {
          setPerfil(p);
          setStep("login");
          setAuthError("Tu usuario está desactivado.");
          await logAuditoria({ accion: "LOGIN_BLOCKED_INACTIVE" });
          await signOut(auth);
          setLoading(false);
          return;
        }

        setPerfil(p);
        setStep("menu");
        setAuthError("");
        await logAuditoria({ accion: "LOGIN_OK" });
        await logAuditoria({ accion: "ENTER_PARTES_MODULE" });
      } catch (e) {
        console.error("Error cargando perfil usuarios_partes:", e);
        setPerfil(null);
        setStep("login");
        setAuthError("Error cargando permisos. Revisa consola.");
        await logAuditoria({
          accion: "LOGIN_OK_PROFILE_LOAD_ERROR",
          extra: String(e?.message || e),
        });
        await signOut(auth);
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const disabledLogin = useMemo(() => {
    return (
      loading ||
      !String(usuario || "").trim() ||
      !String(contrasena || "").trim()
    );
  }, [loading, usuario, contrasena]);

  // ===== Acciones =====
  async function onLoginSubmit(e) {
    e.preventDefault();
    setAuthError("");

    const u = String(usuario || "").trim();
    const p = String(contrasena || "").trim();
    if (!u || !p) return;

    try {
      setLoading(true);
      const email = usuarioToEmail(u);
      await signInWithEmailAndPassword(auth, email, p);
    } catch (e) {
      console.error("LOGIN FAIL:", e);

      const code = String(e?.code || "");
      let msg = "Usuario o contraseña incorrectos, o usuario no existe.";

      if (code === "auth/invalid-credential") {
        msg =
          `Credenciales inválidas. Se intentó iniciar sesión como: ${usuarioToEmail(
            u
          )}. ` + "Revisa que el usuario exista y la contraseña.";
      } else if (code === "auth/user-not-found") {
        msg = `El usuario no existe (${usuarioToEmail(u)}).`;
      } else if (code === "auth/wrong-password") {
        msg = "Contraseña incorrecta.";
      } else if (code === "auth/too-many-requests") {
        msg = "Demasiados intentos. Espera un momento e inténtalo de nuevo.";
      }

      setAuthError(msg);
      await logAuditoria({
        accion: "LOGIN_FAIL",
        extra: String(e?.code || e?.message || e),
      });
      setLoading(false);
    }
  }

  async function salir() {
    await logAuditoria({ accion: "LOGOUT" });
    await signOut(auth);
  }

  // ✅ Crear parte GLOBAL: directo al formulario
  async function irCrear() {
    await logAuditoria({ accion: "MENU_SELECT_CREATE_GLOBAL" });
    navigate(`${partesBase}/nuevo`);
  }

  async function onEditarParte(parte) {
    await logAuditoria({
      accion: "LIST_CLICK_EDIT",
      compania: parte?.compania || null,
      parteId: parte?.id || null,
      folio: typeof parte?.folio === "number" ? parte.folio : null,
    });
    navigate(`${partesBase}/editar?id=${encodeURIComponent(parte.id)}`);
  }

  async function onEliminarParteAuditoria(parte) {
    await logAuditoria({
      accion: "LIST_CLICK_DELETE",
      compania: parte?.compania || null,
      parteId: parte?.id || null,
      folio: typeof parte?.folio === "number" ? parte.folio : null,
    });
  }

  // ===== UI =====
  return (
    <div style={styles.page}>
      <div style={{ ...styles.card, padding: isSmall ? 14 : 18 }}>
        <div style={styles.headerRow}>
          <div>
            <h2 style={{ ...styles.title, fontSize: isSmall ? 18 : 22 }}>
              Partes de Emergencia
            </h2>
            <div style={{ color: "#4b5563", fontSize: isSmall ? 13 : 14 }}>
              Acceso restringido a usuarios autorizados.
            </div>
          </div>

          {perfil && (
            <button onClick={salir} style={styles.btnGhost}>
              Salir
            </button>
          )}
        </div>

        {loading && <div style={{ padding: 12 }}>Cargando...</div>}

        {/* LOGIN */}
        {!loading && step === "login" && (
          <>
            <div style={{ marginTop: 12, ...styles.infoBox }}>
              Ingresa tu <b>usuario</b> y <b>contraseña</b> para acceder al
              módulo de Partes.
            </div>

            {authError && <div style={styles.warn}>{authError}</div>}

            <form onSubmit={onLoginSubmit} style={{ marginTop: 12 }}>
              <label style={styles.label}>Usuario</label>
              <input
                value={usuario}
                onChange={(e) => setUsuario(e.target.value)}
                style={styles.input}
                placeholder="Ej: 16309"
                autoComplete="username"
              />

              <label style={styles.label}>Contraseña</label>
              <input
                value={contrasena}
                onChange={(e) => setContrasena(e.target.value)}
                style={styles.input}
                placeholder="••••••••"
                type="password"
                autoComplete="current-password"
              />

              <button
                type="submit"
                disabled={disabledLogin}
                style={{
                  ...styles.button,
                  opacity: disabledLogin ? 0.6 : 1,
                  cursor: disabledLogin ? "not-allowed" : "pointer",
                }}
              >
                Ingresar
              </button>

              <div style={{ marginTop: 10, color: "#6b7280", fontSize: 12 }}>
                * usuario: <b>{usuarioToEmail(usuario) || "—"}</b>
              </div>
            </form>
          </>
        )}

        {/* MENU ✅ NUEVO: listado desplegado (sin botones viejos) */}
        {!loading && step === "menu" && (
          <>
            <div style={{ marginTop: 12, ...styles.infoBox }}>
              Sesión activa: <b>{perfil?.usuario || "—"}</b> · Rol:{" "}
              <b>{perfil?.rol || "—"}</b>
              {perfil?.rol === "compania" && perfil?.compania ? (
                <>
                  {" "}
                  · Compañía: <b>{perfil.compania}</b>
                </>
              ) : null}
            </div>

            <div style={{ marginTop: 12 }}>
              <button onClick={irCrear} style={styles.menuBtn}>
                ➕ Crear un parte
              </button>
            </div>

            <div style={{ marginTop: 14 }}>
              <div style={{ fontWeight: 900, marginBottom: 8 }}>
                Partes creados
              </div>

              <PartesLista
                perfil={perfil}
                onEditarParte={onEditarParte}
                onEliminarParte={onEliminarParteAuditoria}
              />

              <div style={{ marginTop: 10, color: "#6b7280", fontSize: 13 }}>
                * El botón <b>Eliminar</b> solo aparece para{" "}
                <b>Ayudante General</b> o <b>Admin</b>.
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    padding: 16,
    background: "#0b1220",
  },
  card: {
    width: "min(900px, 96vw)",
    background: "white",
    borderRadius: 14,
    padding: 18,
    boxShadow: "0 10px 30px rgba(0,0,0,.25)",
  },
  headerRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  title: { margin: 0, fontSize: 22 },

  label: { display: "block", marginTop: 12, marginBottom: 6, fontWeight: 700 },

  input: {
    width: "100%",
    padding: 12,
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    outline: "none",
    maxWidth: "100%",
    boxSizing: "border-box",
  },

  button: {
    marginTop: 14,
    width: "100%",
    padding: 12,
    borderRadius: 10,
    border: "none",
    background: "#0f172a",
    color: "white",
    fontWeight: 800,
  },

  btnGhost: {
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    background: "white",
    cursor: "pointer",
    fontWeight: 800,
  },

  menuBtn: {
    width: "100%",
    padding: 12,
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    background: "white",
    cursor: "pointer",
    fontWeight: 900,
    textAlign: "left",
  },

  warn: {
    marginTop: 12,
    padding: 12,
    background: "#fff7ed",
    border: "1px solid #fdba74",
    color: "#7c2d12",
    borderRadius: 10,
  },

  infoBox: {
    padding: 12,
    background: "#f8fafc",
    border: "1px solid #e5e7eb",
    color: "#0f172a",
    borderRadius: 10,
  },
};
